import React, { useState, useRef, useEffect } from 'react';

/**
 * LprRoiEditor — the LPR region-of-interest, as an INLINE field with a deliberate edit mode
 * (unified-viewer-setup-design.md §4.2; David 2026-07-21).
 *
 * Ported from dragon's on-device editor (`dragon/html/config.html` openRoiEditor). The ROI is a
 * NORMALIZED rectangle {x,y,w,h} in [0,1], stored as a CSV string "x,y,w,h" rounded to 3 decimals
 * (dragon's `fmt`). Empty/invalid parses to the full frame. dragon-only (capability-gated).
 *
 * Rendered inline in the Camera → LPR tab (NOT a separate page): a locked read-only preview by
 * default, an "Edit" button unlocks draw/move/resize (mouse + touch) + a Full Frame tool, "Done"
 * re-locks. Committed to the camera form (onChange) at the end of each gesture; the camera's own
 * Save / Revert are the commit / undo. Parent keys this per camera so it re-seeds on selection.
 *
 * The pure round-trip (parseRoi / formatRoi / normalizeRoi) is exported for unit testing.
 */
const MIN_SIZE = 0.01;

/** Round to 3 decimals as a plain string (dragon's fmt). */
const fmt = (n) => (Math.round(n * 1000) / 1000).toString();

/** Clamp to [lo,hi]. */
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/**
 * Parse a "x,y,w,h" CSV to a normalized ROI. Empty/malformed → full frame {0,0,1,1}.
 * @param {string} csv
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export const parseRoi = (csv) => {
  const s = String(csv == null ? '' : csv).trim();
  if (s) {
    const parts = s.split(',').map((p) => parseFloat(p.trim()));
    if (parts.length === 4 && parts.every((n) => !Number.isNaN(n))) {
      return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
    }
  }
  return { x: 0, y: 0, w: 1, h: 1 };
};

/**
 * Normalize a ROI: flip negative w/h, clamp origin to [0,1], keep the box inside the frame with a
 * minimum size (dragon's `normalized`). Pure.
 * @param {{x:number,y:number,w:number,h:number}} roi
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export const normalizeRoi = (roi) => {
  let { x, y, w, h } = roi || {};
  x = x || 0; y = y || 0; w = w || 0; h = h || 0;
  if (w < 0) { x += w; w = -w; }
  if (h < 0) { y += h; h = -h; }
  x = clamp(x, 0, 1);
  y = clamp(y, 0, 1);
  w = clamp(w, MIN_SIZE, 1 - x);
  h = clamp(h, MIN_SIZE, 1 - y);
  return { x, y, w, h };
};

/** A normalized ROI → "x,y,w,h" CSV (3-decimal). Normalizes first. Inverse of parseRoi. */
export const formatRoi = (roi) => {
  const r = normalizeRoi(roi);
  return `${fmt(r.x)},${fmt(r.y)},${fmt(r.w)},${fmt(r.h)}`;
};

// 8 resize handles (corners + edge midpoints), keyed by which edges they move.
const HANDLES = [
  { name: 'nw', edges: 'nw' }, { name: 'n', edges: 'n' }, { name: 'ne', edges: 'ne' },
  { name: 'e', edges: 'e' }, { name: 'se', edges: 'se' }, { name: 's', edges: 's' },
  { name: 'sw', edges: 'sw' }, { name: 'w', edges: 'w' },
];
const handlePos = (name) => {
  const at = { nw: [0, 0], n: [0.5, 0], ne: [1, 0], e: [1, 0.5], se: [1, 1], s: [0.5, 1], sw: [0, 1], w: [0, 0.5] };
  return at[name];
};

const LprRoiEditor = ({ label, hint, value, snapshotUrl, disabled, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [roi, setRoi] = useState(() => normalizeRoi(parseRoi(value)));
  const stageRef = useRef(null);
  const actionRef = useRef(null);
  const roiRef = useRef(roi);

  const putRoi = (r) => { roiRef.current = r; setRoi(r); };
  const commit = (r) => { if (onChange) onChange(formatRoi(r)); };

  // A frame is required to edit the region — painting blind produces false regions (David 2026-07-23).
  // Editing is LOCKED when LPR reading is off OR no snapshot loaded. hasImage resets on camera switch.
  const hasImage = !!snapshotUrl && imgOk;
  const locked = disabled || !hasImage;

  // Re-seed the displayed ROI whenever `value` changes externally (camera switch, Revert). Our own
  // gesture commits set `value` to what we already show, so this is a visual no-op mid-edit and
  // never fights an active drag (value is stable during a drag; only roiRef moves).
  useEffect(() => { const nn = normalizeRoi(parseRoi(value)); roiRef.current = nn; setRoi(nn); }, [value]);
  useEffect(() => { setImgOk(true); }, [snapshotUrl]);  // fresh load attempt for a new camera's frame
  // A locked field (LPR reading off, or no snapshot) can never be in edit mode.
  useEffect(() => { if (locked && editing) setEditing(false); }, [locked, editing]);

  // Normalized pointer position within the frame; handles both mouse and touch events.
  const pt = (e) => {
    const el = stageRef.current;
    if (!el) return { nx: 0, ny: 0 };
    const src = (e.touches && e.touches[0]) ? e.touches[0] : e;
    const rect = el.getBoundingClientRect();
    return {
      nx: clamp((src.clientX - rect.left) / rect.width, 0, 1),
      ny: clamp((src.clientY - rect.top) / rect.height, 0, 1),
    };
  };

  const onStageDown = (e) => {
    if (!editing || e.target !== stageRef.current) return;
    e.preventDefault();
    const { nx, ny } = pt(e);
    actionRef.current = { mode: 'draw', anchor: { x: nx, y: ny } };
    putRoi({ x: nx, y: ny, w: 0, h: 0 });
  };
  const onBodyDown = (e) => {
    if (!editing) return;
    e.preventDefault();
    e.stopPropagation();
    const { nx, ny } = pt(e);
    actionRef.current = { mode: 'move', start: { x: nx, y: ny }, orig: { ...roiRef.current } };
  };
  const onHandleDown = (name) => (e) => {
    if (!editing) return;
    e.preventDefault();
    e.stopPropagation();
    actionRef.current = { mode: 'resize', edges: name, orig: { ...roiRef.current } };
  };

  const onMove = (e) => {
    const a = actionRef.current;
    if (!a) return;
    const { nx, ny } = pt(e);
    if (a.mode === 'draw') {
      putRoi({ x: Math.min(a.anchor.x, nx), y: Math.min(a.anchor.y, ny), w: Math.abs(nx - a.anchor.x), h: Math.abs(ny - a.anchor.y) });
    } else if (a.mode === 'move') {
      const dx = nx - a.start.x;
      const dy = ny - a.start.y;
      const o = a.orig;
      putRoi({ x: clamp(o.x + dx, 0, 1 - o.w), y: clamp(o.y + dy, 0, 1 - o.h), w: o.w, h: o.h });
    } else if (a.mode === 'resize') {
      const o = a.orig;
      let x1 = o.x, y1 = o.y, x2 = o.x + o.w, y2 = o.y + o.h;
      const ed = a.edges;
      if (ed.includes('n')) y1 = ny;
      if (ed.includes('s')) y2 = ny;
      if (ed.includes('w')) x1 = nx;
      if (ed.includes('e')) x2 = nx;
      putRoi({ x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) });
    }
  };
  const endAction = () => {
    if (!actionRef.current) return;
    actionRef.current = null;
    const nn = normalizeRoi(roiRef.current);
    putRoi(nn);
    commit(nn);
  };

  const fullFrame = () => { const nn = { x: 0, y: 0, w: 1, h: 1 }; putRoi(nn); commit(nn); };

  const n = normalizeRoi(roi);
  const box = { left: `${n.x * 100}%`, top: `${n.y * 100}%`, width: `${n.w * 100}%`, height: `${n.h * 100}%` };

  return (
    <div className={'setup-maskfield' + (disabled ? ' setup-maskfield--disabled' : '')}>
      <div className="setup-maskfield__head">
        <div className="setup-maskfield__headleft">
          <div className="setup-maskfield__label">{label}</div>
          {hint ? <div className="setup-maskfield__desc">{hint}</div> : null}
        </div>
        {/* Edit is ALWAYS rendered (disabled when LPR reading is off) so toggling "Read license
            plates" never adds/removes a control and the region frame below can't jump. */}
        <div className="setup-maskfield__headbtns">
          {editing ? <button className="setup-maskfield__toolbtn" onClick={fullFrame}>Full Frame</button> : null}
          <button
            className="setup-maskfield__toolbtn"
            disabled={locked}
            title={disabled ? 'Enable "Read license plates" to set the region.'
              : (!hasImage ? 'No snapshot available — editing unavailable.' : undefined)}
            onClick={() => setEditing((v) => !v)}
          >{editing ? 'Done' : 'Edit'}</button>
        </div>
      </div>

      <div
        ref={stageRef}
        className={'setup-maskfield__frame' + (editing ? ' setup-maskfield__frame--editing' : '')}
        onMouseDown={onStageDown}
        onMouseMove={onMove}
        onMouseUp={endAction}
        onMouseLeave={endAction}
        onTouchStart={onStageDown}
        onTouchMove={onMove}
        onTouchEnd={endAction}
        onTouchCancel={endAction}
      >
        {snapshotUrl && imgOk ? (
          <img className="setup-maskfield__img" src={snapshotUrl} alt="camera snapshot" onError={() => setImgOk(false)} draggable={false} />
        ) : (
          <div className="setup-maskfield__noimg">No snapshot</div>
        )}
        <div className="setup-mask__dim" />
        <div
          data-roi-body
          className="setup-mask__roi"
          style={box}
          onMouseDown={onBodyDown}
          onTouchStart={onBodyDown}
        >
          {editing ? HANDLES.map((h) => {
            const [hx, hy] = handlePos(h.name);
            return (
              <div
                key={h.name}
                data-handle={h.name}
                className="setup-mask__handle"
                style={{ left: `${hx * 100}%`, top: `${hy * 100}%` }}
                onMouseDown={onHandleDown(h.name)}
                onTouchStart={onHandleDown(h.name)}
              />
            );
          }) : null}
        </div>
      </div>
    </div>
  );
};

export default LprRoiEditor;
